import React from "react";
import {
    LuArrowDown,
    LuCheckSquare,
    LuChevronDown, LuChevronUp,
    LuDatabase,
    LuGlasses, LuPencil, LuPlus,
    LuSquare,
    LuTable, LuTrash,
    LuUsers
} from "react-icons/lu";
import {Navigate} from "react-router-dom";
import {Text} from "react-aria-components";
import DropIcon from "../Buttons/DropIcon/DropIcon";
import "./FiltersList.css"
import api from "../../utils/api";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import {SelectedRoles} from "../SelectableRoles/SelectableRoles";
import {LabeledSection} from "../Fields/LabeledTextField.js";
import Notification from "../Notification/Notification";
import {SecondaryButton} from "../Buttons/DefaultButton/DefaultButton";
import {Icon} from "@adobe/react-spectrum";
import IconButton from "../Buttons/IconButton/IconButton.js";


function FilterRow({filter, deleteFilter, setSelected}:{filter: never, deleteFilter: never, setSelected: never}) {
    const [redirectTo, setRedirectTo] = React.useState(null)
    const [selected, setSelectedState] = React.useState(filter.selected)
    const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false)
    const [isCollapsed, setIsCollapsed] = React.useState(true)

    function toggleSelected() {
        setSelected(filter.id, !selected)
        setSelectedState(!selected)
    }

    let icon = <LuSquare className="check-icon" onClick={toggleSelected}/>
    if (selected) {
        icon = <LuCheckSquare className="check-icon" onClick={toggleSelected}/>
    }

    const conditions = []
    filter.conditions.forEach((condition, index) => {
        let operator = condition.operator
        if(operator === "not contains") {
            operator = "doesn't contain"
        }

        conditions.push(<div className="pill condition" key={`${conditions.length}_condition`}>
            <div>{condition.field} {operator}</div>
            <div className="bold">{condition.value}</div>
        </div>)
    })

    const actions = []
    filter.actions.forEach((action, index) => {
        let actionContent = ""
        if (action.action === "Hide Row") {
            actionContent = "hide row"
        } else if (action.action === "Hide Column") {
            actionContent = "hide column " + action.field
            if (action.field === null) {
                actionContent = "hide an unknown column"
            }
        } else if (action.action === "Rename Column") {
            actionContent = "rename " + action.field + " to " + action.value
            if (action.field === null) {
                actionContent = "rename an unknown column to " + action.value
            }
        } else {
            actionContent = "set " + action.field + " to " + action.value
        }
        actions.push(<div className="pill action" key={"action_"+index}>{actionContent}</div>)
    })

    if (redirectTo !== null) {
        return <Navigate to={redirectTo}/>
    }

    let deletionModal = null
    if (showDeleteConfirmation) {
        deletionModal = <ConfirmationModal
            title="Delete Filter"
            message="Are you sure you want to delete this filter?"
            onConfirm={() => {
                deleteFilter(filter.id)
                setShowDeleteConfirmation(false)
            } }
            onCancel={() => { setShowDeleteConfirmation(false) }}
        />
    }

    const dbName = filter.server ? filter.server.name : "deleted Database"
    const tableName = filter.table? filter.table.name : 'unknown table'
    return <>
        {deletionModal}
        <div className="filter-row" onClick={toggleCollapsed => setIsCollapsed(!isCollapsed)}>
            <div className={isCollapsed ? "header row collapsed" : "header row" }>
                <div className="vertically-centered-content check">{icon}</div>
                <div className="vertically-centered-content filter-name">
                    <Text>{filter.name}</Text>
                </div>
                <div className="vertically-centered-content filter-roles">
                    <LuUsers />
                    <SelectedRoles selectedRoles={filter.roles} showLabel={false} />
                </div>

                <div className="vertically-centered-content filter-dbname">
                    <LuTable />
                    <Text>{tableName}</Text>
                </div>
                <div className="vertically-centered-content actions">
                    <IconButton icon={<LuPencil />} onPress={() => {
                        setRedirectTo(`/database/${filter.server.id}/filter/${filter.id}`)
                    }} />
                    <IconButton icon={<LuTrash />} onPress={() => {
                        setShowDeleteConfirmation(true)
                    }} />
                </div>
                <div className="vertically-centered-content collapse">
                    {isCollapsed ? <LuChevronUp /> : <LuChevronDown />}
                </div>
            </div>
            <div className={isCollapsed ? "details row collapsed" : "details row" }>
                { conditions.length > 0 && (
                    <div className="vertically-centered-content filter-row-conditions">{conditions}</div>
                )}
                <div className="vertically-centered-content filter-row-actions">{actions}</div>
            </div>
        </div>
    </>
}

function FiltersList({server, filters, setFilters, returnTo}: {
    server: never,
    filters: never[],
    setFilters: (value: (((prevState: never[]) => never[]) | never[])) => void,
    returnTo?: string
}) {
    function setSelected(filterId, selected) {
        const newFilters = filters.map((filter) => {
            if (filter.id === filterId) {
                filter.selected = selected
            }
            return filter
        })
        filters = newFilters
    }

    function deleteFilter(filterId) {
        const newFilters = filters.filter((filter) => filter.id !== filterId)
        api.deleteFilter(filterId).then(() => {
            filters = newFilters
            setFilters(newFilters)
        })
    }
    function filterRow(filter, filterIndex) {
        return <React.Fragment key={filterIndex} >
            <FilterRow
                filter={filter}
                setSelected={setSelected}
                returnTo={returnTo}
                deleteFilter={deleteFilter}
                key={`filter_${filterIndex}`}
            />
        </React.Fragment>
    }

    let noFiltersNotification = null
    if (filters.length === 0) {
        noFiltersNotification = <>
            <Notification>
                You haven&apos;t created any filters yet. Click &quot;Create Filter&quot; above to get started.
            </Notification>
        </>
    }

    const filterRows = []
    for (let i = 0; i < filters.length; i++) {
        filterRows.push(filterRow(filters[i], i))
    }

    const createFilterButton = <SecondaryButton
        className="filter-row create-filter-button"
        href={`/database/${server.id}/filter/create`}
        text={filterRows.length===0 ? "Create Filter" : <LuPlus />}
        label="Create Filter"
    />
    
    return <>
        <LabeledSection icon={<LuGlasses/>} label="Filters">
            <div className="filters">
                {filterRows}
                {createFilterButton}
            </div>
            {noFiltersNotification}
        </LabeledSection>
    </>
}

export default FiltersList;